import { useEffect, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { fetchBos } from './api';
import type { ModuleOption } from './types';
import './messageLibrary.css';

interface Props {
  open: boolean;
  /** How many messages the assignment will apply to. */
  count: number;
  modules: ModuleOption[];
  onApply: (moduleId: string | null, boId: string | null) => void;
  onClose: () => void;
}

/**
 * Bulk Module / BO assignment.
 *
 * This is the screen's answer to the 390 legacy rows that carry neither: ALPACA
 * declares Module and BO `[RuleRequiredField]` but renders both `AllowEdit="False"`
 * on its detail view, and its seeder never sets them — so the rule is unreachable
 * and every row is unattributed. Rather than invent values from a code scan, the
 * screen lets an admin attribute messages in batches.
 *
 * The BO list CASCADES off the Module, mirroring the legacy `lstBO` collection
 * (the DefaultSetting rows under the module's ItemPath). Changing the Module
 * clears the BO, exactly as the legacy setter does.
 */
export function AssignModuleDialog({ open, count, modules, onApply, onClose }: Props) {
  const [moduleId, setModuleId] = useState('');
  const [boId, setBoId] = useState('');
  const [bos, setBos] = useState<ModuleOption[]>([]);
  const [loadingBos, setLoadingBos] = useState(false);

  // Reset whenever the dialog reopens, so a previous selection never leaks into
  // a new batch.
  useEffect(() => {
    if (open) {
      setModuleId('');
      setBoId('');
      setBos([]);
    }
  }, [open]);

  // The legacy cascade: a new Module means a new BO list and no BO.
  useEffect(() => {
    let alive = true;
    setBoId('');
    if (!moduleId) {
      setBos([]);
      return () => { alive = false; };
    }
    setLoadingBos(true);
    void fetchBos(moduleId)
      .then((rows) => { if (alive) setBos(rows); })
      .finally(() => { if (alive) setLoadingBos(false); });
    return () => { alive = false; };
  }, [moduleId]);

  return (
    <AppDialog
      open={open}
      title="Assign Module"
      icon="ti-tag"
      width={440}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="ca-btn ca-btn-solid"
            disabled={!moduleId}
            onClick={() => onApply(moduleId || null, boId || null)}
          >
            Assign
          </button>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div className="ml-assign">
        <p className="ml-assign-note">
          Set the Module and BO for {count} message{count === 1 ? '' : 's'}.
        </p>

        <label className="ml-assign-row">
          <span className="ml-assign-label">Module</span>
          <select
            className="sd-input"
            value={moduleId}
            onChange={(e) => setModuleId(e.target.value)}
          >
            <option value="">(none)</option>
            {modules.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </label>

        <label className="ml-assign-row">
          <span className="ml-assign-label">BO</span>
          <select
            className="sd-input"
            value={boId}
            disabled={!moduleId || loadingBos}
            onChange={(e) => setBoId(e.target.value)}
          >
            <option value="">{loadingBos ? 'Loading…' : '(none)'}</option>
            {bos.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </label>

        <p className="ml-assign-hint">
          BO is optional, and lists only the forms under the chosen Module.
        </p>
      </div>
    </AppDialog>
  );
}

export default AssignModuleDialog;
